/**
 * A church's own artwork and full recording for a song (docs/15-MUSIC-AND-AI.md,
 * "A church's own artwork and recording"): what it may upload, decided the
 * same way on both sides — the API refuses what this refuses, and the web's
 * upload offers only this (its `accept` list, its hint, its refusals).
 *
 * Only what the church uploads itself. Apple's artwork and previews are never
 * copied into our storage (the Apple Developer Program License Agreement,
 * §3.3.6(D)); these sit beside them and win where both exist.
 */

export const SONG_MEDIA_KINDS = ['artwork', 'audio'] as const
export type SongMediaKind = (typeof SONG_MEDIA_KINDS)[number]

const MB = 1024 * 1024

/** The largest file of each kind. */
export const SONG_MEDIA_MAX_BYTES: Record<SongMediaKind, number> = {
  /** Artwork as series art is: what a screen needs, not a camera original. */
  artwork: 10 * MB,
  /**
   * A recording, as large as any plan file may be (plan-files.service.ts):
   * ten minutes of WAV, or an hour of MP3.
   */
  audio: 100 * MB,
}

/** The longest recording a confirm may claim: anything longer is a misread length. */
export const SONG_AUDIO_MAX_DURATION_MS = 4 * 60 * 60 * 1000

/** Artwork's sides, in pixels: at least this, and no further from square than `ARTWORK_MAX_RATIO`. */
export const ARTWORK_MIN_SIDE = 300
export const ARTWORK_MAX_SIDE = 20_000
export const ARTWORK_MAX_RATIO = 1.25

/** The types the API accepts, each with its key's extension. */
const TYPES: Record<SongMediaKind, Record<string, string>> = {
  artwork: { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' },
  audio: { 'audio/mpeg': 'mp3', 'audio/mp4': 'm4a', 'audio/aac': 'aac', 'audio/wav': 'wav' },
}

/** What browsers and systems also call them. */
const ALIASES: Record<string, string> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'audio/mp3': 'audio/mpeg',
  'audio/mpeg3': 'audio/mpeg',
  'audio/x-mpeg': 'audio/mpeg',
  'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/x-aac': 'audio/aac',
  'audio/aacp': 'audio/aac',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
}

/** By a file's name, when the browser says nothing useful. */
const BY_EXTENSION: Record<SongMediaKind, Record<string, string>> = {
  artwork: { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' },
  audio: { mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav' },
}

/** The upload's `accept` list. */
export const SONG_MEDIA_ACCEPT: Record<SongMediaKind, string> = {
  artwork: '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp',
  audio: '.mp3,.m4a,.aac,.wav,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav',
}

/** The upload's hint: what it can be, and how large. */
export const SONG_MEDIA_HINT: Record<SongMediaKind, string> = {
  artwork: 'JPEG, PNG or WebP, square, up to 10 MB',
  audio: 'MP3, M4A, AAC or WAV, up to 100 MB',
}

/** Said beside both uploads (muted): the church answers for what it shares. */
export const SONG_MEDIA_RIGHTS_LINE =
  'Only upload music your church has the rights to share with its team.'

const TYPE_MESSAGE: Record<SongMediaKind, string> = {
  artwork: 'Artwork must be a JPEG, PNG or WebP image.',
  audio: 'A recording must be MP3, M4A, AAC or WAV.',
}

const SIZE_MESSAGE: Record<SongMediaKind, string> = {
  artwork: 'Artwork must be 10 MB or smaller.',
  audio: 'A recording must be 100 MB or smaller.',
}

const bare = (contentType: string | null | undefined) =>
  (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? ''

const extensionOf = (fileName: string | null | undefined) =>
  /\.([a-z0-9]+)$/i.exec(fileName ?? '')?.[1]?.toLowerCase() ?? ''

/** The types the API accepts for a kind (exactly these: aliases are the client's to resolve). */
export const songMediaTypes = (kind: SongMediaKind): readonly string[] => Object.keys(TYPES[kind])

/**
 * The type to upload a file as: the browser's (its other names resolved:
 * `audio/x-m4a` is `audio/mp4`), else what the file's name says — or null
 * when it is not something this kind takes.
 */
export function songMediaUploadType(
  kind: SongMediaKind,
  browserType: string | null | undefined,
  fileName: string | null | undefined,
): string | null {
  const said = bare(browserType)
  const type = ALIASES[said] ?? said
  if (type in TYPES[kind]) return type
  // An empty or generic type ("application/octet-stream"): the name decides.
  if (said === '' || said === 'application/octet-stream')
    return BY_EXTENSION[kind][extensionOf(fileName)] ?? null
  return null
}

/** The extension a key of this type gets (`audio/mp4` → `m4a`). Throws on a type it does not take. */
export function songMediaExtension(kind: SongMediaKind, contentType: string): string {
  const ext = TYPES[kind][bare(contentType)]
  if (!ext) throw new Error(`not a ${kind} type: ${contentType}`)
  return ext
}

/** Why a file can't be this kind, in words — or null when it can. Type first, then size. */
export function songMediaProblem(
  kind: SongMediaKind,
  file: { contentType: string | null; sizeBytes: number | null },
): string | null {
  if (!(bare(file.contentType) in TYPES[kind])) return TYPE_MESSAGE[kind]
  if (file.sizeBytes !== null && file.sizeBytes > SONG_MEDIA_MAX_BYTES[kind])
    return SIZE_MESSAGE[kind]
  return null
}

/** The messages on their own, for an API that checks type and size at different steps. */
export const songMediaMessages = (kind: SongMediaKind) => ({
  type: TYPE_MESSAGE[kind],
  size: SIZE_MESSAGE[kind],
})

/**
 * Why an image can't be a song's artwork by its shape — or null. Artwork
 * shows square everywhere (cropped to fit), so a wide banner would lose its
 * sides: square or nearly (within 5:4), and large enough to be sharp.
 */
export function artworkShapeProblem(width: number, height: number): string | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
    return 'That image could not be read.'
  const size = `${Math.round(width)} × ${Math.round(height)}`
  if (Math.min(width, height) < ARTWORK_MIN_SIDE)
    return `Artwork must be at least ${ARTWORK_MIN_SIDE} × ${ARTWORK_MIN_SIDE} pixels: this image is ${size}.`
  if (Math.max(width, height) > ARTWORK_MAX_SIDE) return `That image is too large (${size}).`
  if (Math.max(width, height) / Math.min(width, height) > ARTWORK_MAX_RATIO)
    return `Artwork must be square, or nearly: this image is ${size}.`
  return null
}

/** A recording's length as read by the browser, made whole — or null when it is not believable. */
export function audioDurationMs(seconds: number | null | undefined): number | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  const ms = Math.round(seconds * 1000)
  return ms <= SONG_AUDIO_MAX_DURATION_MS ? ms : null
}

/** "1:05", "1:02:03" — a player's elapsed or total time (never rounded up past where it is). */
export function playerTime(seconds: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const rest = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${rest}` : `${m}:${rest}`
}
